/**
 * THE PERSONA-HEALTH DISPLAY, EXTRACTED SO IT CANNOT DIVERGE AGAIN (task-es9vcil1gn).
 *
 * ⛔⛔ WHY THIS IS A MODULE AND NOT A SECOND COPY. #4719 (task-em3cnl32tq) taught the persona card to
 * distinguish THREE STATES THAT PREVIOUSLY LOOKED IDENTICAL — never researched / researched but
 * weightless / researched and complete — and they have three different remedies. It landed INLINE in
 * pages/CategoryResearch.tsx, on ONE of the TWO surfaces that render personas. The other one,
 * pages/admin/CategoryResearch.tsx, kept showing name + description only.
 * ⇒ A PARTIAL FIX ACROSS TWO SURFACES IS WORSE THAN NO FIX: after #4719 the blind page was silently
 * the stale one, with nothing on it saying a better view existed. Before, both were equally blind and
 * nobody was misled about which was authoritative.
 * ⇒ AND THE BLIND PAGE IS THE DISCOVERABLE ONE. /admin/category-research is in the sidebar
 * (components/layout/navigation-items.ts:319, sidebar.tsx:1417); /research/category-perceptions —
 * the page #4719 improved — has NO nav entry and is reached only from the help guide and the runbook.
 * So the default click path landed on the surface that could not tell the three states apart.
 * Re-implementing the badges on the second page would have recreated exactly that divergence with
 * extra steps, which is why the display lives here and both pages import it.
 *
 * ⚠️ DISPLAY ONLY — NOTHING HERE WRITES. The perceptions page owns a labelled "Re-research personas"
 * control; it passes it in as `action`. This module never renders a control of its own, and the
 * empty-state copy is derived from whether an `action` was supplied (see PersonaEmptyState) so a
 * surface without the button never tells the operator to press it.
 */
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

/** The persona shape as it reaches the client: category_characteristics.personas, passed through raw
 *  by routes/category-characteristics.js:198 — benefit_weights and life_stage are NOT stripped. */
export interface PersonaLike {
    persona_key?: string;
    name?: { en?: string; es?: string } | string;
    /** The admin preview this replaced read `p.name?.en || p.persona_name || p.name`. Kept so the
     *  extraction is not a quiet behaviour change — see nameCandidates for the measured cohort. */
    persona_name?: { en?: string; es?: string } | string;
    description?: { en?: string } | string;
    benefit_weights?: Record<string, unknown> | null;
    life_stage?: string | string[] | null;
}

/**
 * Every string a persona might be NAMED by, in the order the old surfaces preferred them.
 *
 * ⚠️ MEASURED BEFORE BEING DEFENDED, because "other consumers handle this field" is not evidence
 * about THIS table: of the 9,410 persona objects in `category_characteristics.personas` on db2
 * (2026-08-02, read-only), **0 carry `persona_name` and 9,410 carry `name`**. Those other consumers
 * read `sector_personas` and `persona_benefit_mappings`, which are different tables with a different
 * shape. So this is defence, not a live path — recorded so the next reader does not mistake it for
 * one, and does not delete it thinking it was never justified either.
 * ⇒ IT IS STILL WORTH KEEPING: it is what the admin preview did before this extraction, and the one
 * thing this card must never do is render a persona as a blank.
 *
 * ⛔ IT RETURNS EVERY NAME, NOT THE PREFERRED ONE. Collapsing each field to a single string here
 * (`v?.en || v?.es`) silently broke the SPANISH echo check — a persona named
 * `{ en: 'Saver', es: 'María Ahorradora' }` resolved to 'Saver' and the echo went unflagged. Display
 * wants a preference order; detection wants the whole set. One list serves both only if it keeps
 * everything and the CALLER picks. Caught by the existing es-echo test, which is why that test exists.
 */
const nameCandidates = (p: PersonaLike): (string | undefined)[] => {
    const texts = (v: PersonaLike['name']): (string | undefined)[] =>
        typeof v === 'string' ? [v] : [v?.en, v?.es];
    return [...texts(p.name), ...texts(p.persona_name), p.persona_key];
};

/** life_stage may be a scalar or an array; absent on every un-rebuilt row. */
export const lifeStageOf = (p: PersonaLike): string | null => {
    const ls = p.life_stage;
    if (!ls) return null;
    return Array.isArray(ls) ? ls.join(', ') : String(ls);
};

/** benefit_weights IS the routing predicate — an empty object is a persona that cannot route. */
export const benefitWeightCount = (p: PersonaLike): number =>
    p.benefit_weights ? Object.keys(p.benefit_weights).length : 0;

export const weightlessCountOf = (personas: PersonaLike[]): number =>
    personas.filter((p) => benefitWeightCount(p) === 0).length;

/**
 * The echo tell: budget_conscious_maria / "María Ahorradora" is the old prompt's baked-in example.
 * Its presence means this category was NOT rebuilt (see #4708).
 */
export const isEchoPersona = (p: PersonaLike): boolean =>
    // ⛔ CHECKED ACROSS EVERY NAME FIELD, NOT JUST `name`. A legacy row is exactly the kind that both
    // carries the echo name AND stores it under an older key, so a flag that only reads `name` would
    // go quiet on the rows it exists to catch.
    nameCandidates(p).some((n) => n === 'budget_conscious_maria'
        || n === 'Budget-Conscious Maria'
        || n === 'María Ahorradora');

/**
 * ⛔ NEVER RETURNS AN EMPTY STRING. An unrecognised persona shape must say so, not render as a blank
 * tile with badges floating beside nothing — a blank reads as a rendering glitch, and the operator
 * has no way to tell it from a persona genuinely called "". This is the general form of the
 * `persona_name` case: the fix is a fallback that holds for a key nobody has met yet, not one more
 * key added to a list.
 */
const displayName = (p: PersonaLike): string =>
    nameCandidates(p).find((n) => typeof n === 'string' && n.trim() !== '') || '(unnamed persona)';

const displayDescription = (p: PersonaLike): string | null => {
    if (typeof p.description === 'string') return p.description;
    return p.description?.en || null;
};

/**
 * The at-a-glance health badges: how many personas, whether they can route, and what the research was
 * grounded on.
 *
 * ⛔ `grounding` CARRIES A NULL-VERSUS-ZERO CONTRACT AND IT MUST SURVIVE THIS COMPONENT.
 * null = the count was never recorded (UNKNOWN). 0 = the personas were invented from general
 * knowledge rather than the catalogue (KNOWN, and bad). Collapsing them — `grounding || 0`, a falsy
 * test, a default parameter — would render an unknown as a confident accusation. Hence `== null`.
 */
export function PersonaHealthBadges({ personas, count, grounding, showCount = true }: {
    personas: PersonaLike[];
    /** The server's own count. Passed explicitly rather than derived, so the badge keeps reporting
     *  the same number the aspect reports. */
    count: number;
    grounding: number | null;
    /** Suppress only the count badge, for a host that already prints the count beside these badges.
     *  ⚠️ It never suppresses the HEALTH badges — those are the point. */
    showCount?: boolean;
}) {
    const weightless = weightlessCountOf(personas);
    return (
        <>
            {showCount && (
                <Badge variant="default" className="text-xs">
                    {count} personas
                </Badge>
            )}
            {/* Completeness — the routing predicate. Empty is an explicit state, never a blank. */}
            {weightless === 0 ? (
                <Badge variant="default" className="text-xs bg-green-600 hover:bg-green-600">
                    <CheckCircle className="w-3 h-3 mr-1" /> all have benefit weights
                </Badge>
            ) : (
                <Badge variant="destructive" className="text-xs">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {weightless} of {personas.length} have NO benefit weights
                </Badge>
            )}
            {/* Grounding — 0 products means invented, not researched; null means we do not know. */}
            {grounding == null ? (
                <Badge variant="outline" className="text-xs">grounding not recorded</Badge>
            ) : grounding === 0 ? (
                <Badge variant="destructive" className="text-xs">
                    <AlertCircle className="w-3 h-3 mr-1" /> researched on 0 products (invented)
                </Badge>
            ) : (
                <Badge variant="secondary" className="text-xs">
                    grounded on {grounding} product{grounding === 1 ? '' : 's'}
                </Badge>
            )}
        </>
    );
}

/** One persona tile: name, the echo warning, life stage, benefit-weight count, description. */
export function PersonaTile({ persona }: { persona: PersonaLike }) {
    const ls = lifeStageOf(persona);
    const weightCount = benefitWeightCount(persona);
    const echo = isEchoPersona(persona);
    const description = displayDescription(persona);
    return (
        <div
            className={`rounded-lg p-3 border ${echo ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/30' : 'bg-muted/50 border-transparent'}`}
        >
            <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm">{displayName(persona)}</span>
                {echo && (
                    <Badge variant="destructive" className="text-[10px]" title="This name is the old prompt's baked-in example — its presence means this category was not rebuilt (#4708).">
                        ⚠ echo name — not rebuilt
                    </Badge>
                )}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                {/* life_stage — absent on every un-rebuilt row today. */}
                {ls ? (
                    <Badge variant="secondary" className="text-[10px]">life stage: {ls}</Badge>
                ) : (
                    <Badge variant="outline" className="text-[10px] text-muted-foreground">no life stage</Badge>
                )}
                {/* benefit_weights — the routing predicate. Empty is explicit. */}
                {weightCount > 0 ? (
                    <Badge variant="secondary" className="text-[10px]">{weightCount} benefit weights</Badge>
                ) : (
                    <Badge variant="destructive" className="text-[10px]">
                        <XCircle className="w-3 h-3 mr-1" /> no benefit weights
                    </Badge>
                )}
            </div>
            {description && (
                <div className="text-xs text-muted-foreground mt-1.5">
                    {description.substring(0, 80)}...
                </div>
            )}
        </div>
    );
}

export function PersonaGrid({ personas }: { personas: PersonaLike[] }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {personas.map((persona, i) => (
                <PersonaTile key={i} persona={persona} />
            ))}
        </div>
    );
}

/**
 * The "no personas" copy.
 * ⚠️ `hasAction` IS NOT COSMETIC. The perceptions page carries a labelled "Re-research personas"
 * button and its empty state names it. The admin surface is display-only for personas, so naming a
 * button that is not there would send the operator looking for a control that does not exist — a
 * smaller version of the same confidently-wrong-surface defect this extraction exists to close.
 */
export function PersonaEmptyState({ hasAction }: { hasAction: boolean }) {
    return (
        <p className="text-sm text-muted-foreground">
            {hasAction
                ? 'Not researched yet — this category has no buyer personas. Use "Re-research personas" above to create them.'
                : 'Not researched yet — this category has no buyer personas.'}
        </p>
    );
}
